import * as React from 'react'
import { Archive, MapPin, Pencil } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DetailPage } from '@/components/templates/detail-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogFooter,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { displayDateTime } from '@/lib/dates'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, patchJSON } from '@/lib/api'
import { ChapterMembersSection } from './ChapterMembersSection'
import { ChapterLeadersSection } from './ChapterLeadersSection'
import { ProfileExtras } from './ChapterProfileExtras'

export interface ChapterRecord {
  id: number
  name: string
  description: string
  meeting_location: string
  contact_email: string
  quickbooks_class: string
  quickbooks_class_id?: string | null
  quickbooks_class_name?: string | null
  public_visibility: boolean
  created_at: string
  active_member_count: number
  tagged_contact_count: number
  archived: boolean
}

interface Props {
  chapterId: number
  onBack: () => void
}

export function ChapterRecordPage({ chapterId, onBack }: Props) {
  const client = useQueryClient()
  const [editing, setEditing] = React.useState(false)
  const [archiveOpen, setArchiveOpen] = React.useState(false)
  const chapterQuery = useQuery({
    queryKey: ['chapter', chapterId],
    queryFn: () => getJSON<{ chapter: ChapterRecord }>(`/api/v1/chapters/${chapterId}`),
    retry: false,
  })
  const classesQuery = useQuickBooksClasses()
  const chapter = chapterQuery.data?.chapter
  const update = useMutation({
    mutationFn: (attributes: Partial<ChapterRecord>) =>
      patchJSON<{ chapter: ChapterRecord }>(`/api/v1/chapters/${chapterId}`, {
        chapter: attributes,
      }),
    onSuccess: (data) => {
      client.setQueryData(['chapter', chapterId], data)
      client.invalidateQueries({ queryKey: ['chapters'] })
      setEditing(false)
    },
  })
  const transition = useMutation({
    mutationFn: (action: 'archive' | 'restore') =>
      patchJSON<{ chapter: ChapterRecord }>(`/api/v1/chapters/${chapterId}/${action}`, {}),
    onSuccess: (data) => {
      client.setQueryData(['chapter', chapterId], data)
      client.invalidateQueries({ queryKey: ['chapters'] })
      setArchiveOpen(false)
    },
  })

  if (chapterQuery.isLoading)
    return <div className="p-8 text-body-sm text-muted-foreground">Loading chapter…</div>
  if (chapterQuery.isError || !chapter)
    return <Alert className="m-8">Unable to load this chapter.</Alert>

  return (
    <DetailPage
      title={chapter.name}
      subtitle={chapter.contact_email || undefined}
      backHref="/contacts/chapters"
      backLabel="Chapters"
      onBack={onBack}
      badge={
        chapter.archived ? (
          <Badge>Archived</Badge>
        ) : (
          <Badge variant={chapter.public_visibility ? 'info' : 'neutral'}>
            {chapter.public_visibility ? 'Listed' : 'Not listed'}
          </Badge>
        )
      }
      actions={
        <Button
          variant={chapter.archived ? 'outline' : 'quietDestructive'}
          size="sm"
          onClick={() => (chapter.archived ? transition.mutate('restore') : setArchiveOpen(true))}
        >
          <Archive className="mr-2 size-4" aria-hidden="true" />
          {chapter.archived ? 'Unarchive' : 'Archive'}
        </Button>
      }
    >
      <Card className="overflow-hidden">
        <CardContent className="divide-y divide-border p-0">
          {chapter.archived && (
            <div className="px-4 pt-4">
              <Alert variant="warning">
                Archived chapter. Member tags, event tags, and email history stay here; pickers,
                directory filters, the Segments builder, and leader tools are off.
              </Alert>
            </div>
          )}
          <section className="px-4 py-4">
            <div className="flex items-start gap-4">
              <div
                className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
                aria-hidden="true"
              >
                <MapPin className="size-4" />
              </div>
              <dl className="grid flex-1 grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4">
                <Fact
                  label="Members"
                  value={`${chapter.active_member_count} active · ${chapter.tagged_contact_count} tagged`}
                />
                <Fact label="Leaders" value="0" />
                <Fact label="Created" value={displayDateTime(chapter.created_at)} />
                <Fact
                  label="Public page"
                  value={chapter.public_visibility && !chapter.archived ? 'Listed' : 'Not listed'}
                />
              </dl>
            </div>
          </section>
          <ProfileSection
            chapter={chapter}
            editing={editing}
            disabled={chapter.archived}
            onEdit={() => setEditing(true)}
            onCancel={() => setEditing(false)}
            onSave={(values) => update.mutate(values)}
            saving={update.isPending}
            error={updateError(update.isError)}
            quickbooksClasses={availableQuickBooksClasses(classesQuery.data)}
          />
          <ChapterMembersSection chapterId={chapterId} readOnly={chapter.archived} />
          <ChapterLeadersSection chapterId={chapterId} readOnly={chapter.archived} />
        </CardContent>
      </Card>
      <ChapterArchiveDialog
        name={chapter.name}
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        onArchive={() => transition.mutate('archive')}
      />
    </DetailPage>
  )
}

function updateError(isError: boolean) {
  return isError ? 'Unable to save the profile. Check your changes and try again.' : undefined
}

function ChapterArchiveDialog({
  name,
  open,
  onOpenChange,
  onArchive,
}: {
  name: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onArchive: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive {name}?</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5 text-body-sm">
          <p>Stays: member tags, event tags, and email history.</p>
          <p>Stops: pickers, the directory filter, the Segments builder, and leader tools.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onArchive}>
            Archive chapter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-caption uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-body-sm text-foreground">{value}</dd>
    </div>
  )
}

function ProfileSection({
  chapter,
  editing,
  disabled,
  onEdit,
  onCancel,
  onSave,
  saving,
  error,
  quickbooksClasses,
}: {
  chapter: ChapterRecord
  editing: boolean
  disabled: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: (values: Partial<ChapterRecord>) => void
  saving: boolean
  error?: string
  quickbooksClasses: Array<{ id: string; name: string }>
}) {
  const [values, setValues] = React.useState(() => normalizeChapter(chapter))
  React.useEffect(() => setValues(normalizeChapter(chapter)), [chapter])
  const field = (
    key: 'name' | 'description' | 'meeting_location' | 'contact_email',
    label: string,
  ) => (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
      <Label htmlFor={`chapter-${key}`} className="text-body-sm text-muted-foreground sm:w-40">
        {label}
      </Label>
      {editing ? (
        key === 'description' ? (
          <Textarea
            id={`chapter-${key}`}
            value={values[key]}
            onChange={(event) => setValues({ ...values, [key]: event.target.value })}
          />
        ) : (
          <Input
            id={`chapter-${key}`}
            value={values[key]}
            onChange={(event) => setValues({ ...values, [key]: event.target.value })}
          />
        )
      ) : (
        <span className="text-body-sm text-foreground">{values[key] || '—'}</span>
      )}
    </div>
  )
  return (
    <section className="px-4 py-4">
      <div className="flex items-center justify-between gap-3 pb-3">
        <h3 className="text-body font-medium">Profile</h3>
        {editing ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saving}
              onClick={() =>
                onSave({
                  name: values.name,
                  description: values.description,
                  meeting_location: values.meeting_location,
                  contact_email: values.contact_email,
                  quickbooks_class_id: values.quickbooks_class_id || null,
                  public_visibility: values.public_visibility,
                })
              }
            >
              Save
            </Button>
          </div>
        ) : (
          !disabled && (
            <Button variant="ghost" size="icon" aria-label="Edit profile" onClick={onEdit}>
              <Pencil className="size-4" aria-hidden="true" />
            </Button>
          )
        )}
      </div>
      {error && (
        <Alert variant="destructive" className="mb-3">
          {error}
        </Alert>
      )}
      <div className="flex flex-col gap-3">
        {field('name', 'Name')}
        {field('description', 'Description')}
        {field('meeting_location', 'Meeting location')}
        {field('contact_email', 'Contact email')}
        <QuickBooksClassField
          chapter={chapter}
          editing={editing}
          classes={quickbooksClasses}
          value={values.quickbooks_class_id ?? ''}
          onChange={(value) => setValues({ ...values, quickbooks_class_id: value })}
        />
        <ProfileExtras
          editing={editing}
          publicVisibility={values.public_visibility}
          onVisibilityChange={(checked) => setValues({ ...values, public_visibility: checked })}
        />
      </div>
    </section>
  )
}

function QuickBooksClassField({
  chapter,
  editing,
  classes,
  value,
  onChange,
}: {
  chapter: ChapterRecord
  editing: boolean
  classes: Array<{ id: string; name: string }>
  value: string
  onChange: (value: string) => void
}) {
  const stored = chapter.quickbooks_class_id
    ? { id: chapter.quickbooks_class_id, name: chapter.quickbooks_class_name ?? '' }
    : null
  const missing = stored && !classes.some((item) => item.id === stored.id)
  if (!stored && classes.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
      <span className="text-body-sm text-muted-foreground sm:w-40">QuickBooks Class</span>
      {editing ? (
        <Select
          value={value || 'unmapped'}
          onValueChange={(next) => onChange(next === 'unmapped' ? '' : next)}
        >
          <SelectTrigger aria-label="QuickBooks class" className="h-9 max-w-md">
            <SelectValue placeholder="Choose a QuickBooks Class" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unmapped">No Class</SelectItem>
            {missing && stored && (
              <SelectItem value={stored.id}>{stored.name} (not found in QuickBooks)</SelectItem>
            )}
            {classes.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="text-body-sm text-foreground">
          {stored?.name || '—'}
          {missing && (
            <span className="ml-2 text-caption text-muted-foreground">not found in QuickBooks</span>
          )}
        </span>
      )}
    </div>
  )
}

function normalizeChapter(chapter: ChapterRecord): ChapterRecord {
  return {
    ...chapter,
    description: chapter.description || '',
    meeting_location: chapter.meeting_location || '',
    contact_email: chapter.contact_email || '',
    quickbooks_class: chapter.quickbooks_class || '',
    quickbooks_class_id: chapter.quickbooks_class_id || null,
    quickbooks_class_name: chapter.quickbooks_class_name || null,
  }
}

function useQuickBooksClasses() {
  return useQuery({
    queryKey: ['quickbooks-classes'],
    queryFn: () =>
      getJSON<{ quickbooks_classes: Array<{ id: string; name: string }> }>(
        '/api/v1/chapters/quickbooks_classes',
      ),
    retry: false,
  })
}

function availableQuickBooksClasses(data?: {
  quickbooks_classes: Array<{ id: string; name: string }>
}) {
  return data?.quickbooks_classes ?? []
}
